import * as shpwrite from "@mapbox/shp-write";
import { CUSTOM_BUTTON, LAYER_CONFIG } from "../../../../app/GIS/constant";
import { RandomizationTypeEnum, RandomizationMethodEnum } from "../enums";
import { Polygon, SpatialReference, Geometry, Polyline, Point } from '@arcgis/core/geometry';
import * as offsetOperator from '@arcgis/core/geometry/operators/offsetOperator.js';
import * as projection from '@arcgis/core/geometry/projection.js';
import Graphic from '@arcgis/core/Graphic';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import { MAPOPERATION_TYPE } from "../../../../app/GIS/constant";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { IHeatmapLegendData } from "../models";
import { HEATMAP_TYPE } from "../../../../app/GIS/constant";
import { helperFunctions } from "./helper";
// import JSZip from "jszip";

// interface IScreenPoint {
//     x: number,
//     y: number
// }

// interface IScreenArea {
//     x: number,
//     y: number,
//     width: number,
//     height: number
// }

// interface ExportOptions {
//     width?: number;
//     height?: number;
//     padding?: number;
//     backgroundColor?: string;
//     polygonOpacity?: number;
// }

// interface Bounds {
//     xmin: number;
//     ymin: number;
//     xmax: number;
//     ymax: number;
// }

// interface Transform {
//     scale: number;
//     offsetX: number;
//     offsetY: number;
//     bounds: Bounds;
// }

// const SCREEN_PADDING_PX = 10;


export const gisHelperFunctions = {

    getESRILayersOrderedList() {
        // add the layers in bottom to top order.
        return [
            LAYER_CONFIG.REFERENCE.name,
            LAYER_CONFIG.GRID.name,
            LAYER_CONFIG.BLOCK.name,
            LAYER_CONFIG.BORDER.name,
            LAYER_CONFIG.FILLER.name,
            LAYER_CONFIG.PRESCRIPTION.name,
            LAYER_CONFIG.SKETCHVIEW.name
        ];
    },

    createAndDownloadShapeFile(geojson: any, fileName: string) {
        try {
            shpwrite.download(geojson, {
                folder: fileName,
                filename: fileName,
                types: {
                    point: "points",
                    polygon: fileName,
                    line: "lines"
                },
                compression: "STORE",
                outputType: "blob"
            });
            return true;

        } catch (error) {
            console.log("geoJson to shapFile error: ", error);
            return false;
        }
    },
    
    // async NewcreateAndDownloadShapeFile(
    //     geojson: any,
    //     fileName: string,
    //     mode: string = "zip"
    // ): Promise<boolean> {
    //     const options = {
    //         folder: fileName,
    //         filename: fileName,
    //         types: { point: "points", polygon: fileName, line: "lines" },
    //         compression: "STORE",
    //         outputType: "blob",
    //     } as any;

    //     const canPickFolder = typeof (window as any).showDirectoryPicker === "function";

    //     try {
    //         if (mode === "folder" && canPickFolder) {
    //             // Open the picker first, while the click's user activation is still valid
    //             const rootDir = await (window as any).showDirectoryPicker({ mode: "readwrite" });

    //             const zipBlob = (await shpwrite.zip(geojson, options)) as Blob;
    //             const zip = await JSZip.loadAsync(zipBlob);
    //             const targetDir = await rootDir.getDirectoryHandle(fileName, { create: true });

    //             for (const entry of Object.values(zip.files)) {
    //                 if (entry.dir) continue;
    //                 // Entries are stored as "<folder>/<file>", so keep only the file name
    //                 const name = entry.name.split("/").pop() as string;
    //                 const fileHandle = await targetDir.getFileHandle(name, { create: true });
    //                 const writable = await fileHandle.createWritable();
    //                 await writable.write(await entry.async("blob"));
    //                 await writable.close();
    //             }
    //             return true;
    //         }

    //         // Zip download (default, and fallback for browsers without a folder picker)
    //         const zipBlob = (await shpwrite.zip(geojson, options)) as Blob;
    //         saveAs(zipBlob, `${fileName}.zip`);
    //         return true;
    //     } catch (error: any) {
    //         if (error?.name === "AbortError") {
    //             return false; // user closed the picker
    //         }
    //         console.error("geoJson to shapefile error: ", error);
    //         return false;
    //     }
    // },

    getUniqueId() {
        return (
            Math.random().toString(36).substring(2) +
            new Date().getTime().toString(36)
        );
    },

    getGridLabelAngle(theta: number) {
        if (theta < 0) {
            theta = 360 + theta;
        }
        const t = theta % 360;
        if (t <= 90) return 360 - t;
        if (t <= 180) return 180 - t;
        if (t <= 270) return 540 - t; // 360 - (t - 180);
        return 360 - t;
    },

    // getBlockLabelAngle(theta: number) {
    //     if(theta <= 90) return 90 - theta;
    //     else if(theta <= 180) return 360 - (theta - 90);
    //     else if(theta <= 270) return 270 - (theta - 180);
    //     else if(theta <= 360) return 180 - (theta - 270);
    //     return 90;
    // },

    getLineLength(xmin: number, ymin: number, xmax: number, ymax: number) {
        return Math.sqrt(Math.pow(ymax - ymin, 2) + Math.pow(xmax - xmin, 2));
    },

    getCoordinates(theta: number, x1: number, y1: number, gridLength: number, gridWidth: number, subPlot: number) {
        const rad = theta * Math.PI / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const width = gridWidth / subPlot;
        // Bottom left
        const bl = [x1, y1];
        // Top left
        const tl = [x1 + gridLength * cos, y1 + gridLength * sin];
        // Bottom right
        const br = [x1 + width * sin, y1 - width * cos];
        // Top right
        const tr = [tl[0] + width * sin, tl[1] - width * cos];
        const rings = [
            [bl, tl, tr, br, bl]
        ];
        return rings;
    },

    calculateSlope(xmin: number, ymin: number, xmax: number, ymax: number) {
        if (xmax === xmin && ymax === ymin) {
            throw new Error("Invalid points: both points are identical.");
        }

        let theta = Math.atan2(ymax - ymin, xmax - xmin) * (180 / Math.PI);  // atan2 returns angle with direction
        if (theta < 0) {
            theta += 360;
        }
        return theta;
    },

    checkIsManuallyRandomizedRep(trial?: any, rep?: any): boolean {
        return (
            (trial?.randomizationType === RandomizationTypeEnum.ADAPTIVE || trial?.randomizationType === RandomizationTypeEnum.RCB) &&
            rep?.repRandomizationMethod === RandomizationMethodEnum.Manual
        );
    },

    arrangeRingCoordinatesWithClickedVertices(ringCoordinates: number[][], clickedVertices: number[][]) {
        const arrangedCoordinates: number[][] = [];
        clickedVertices.forEach((clickVertex) => {
            let nearestIndex = -1;
            let minDistance = Infinity;
            const remainingRingCoordinates = [...ringCoordinates];
            remainingRingCoordinates.forEach((ringVertex, index) => {
                const distance = gisHelperFunctions.getLineLength(
                    ringVertex[0],
                    ringVertex[1],
                    clickVertex[0],
                    clickVertex[1]
                );
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestIndex = index;
                }
            });

            if (nearestIndex !== -1) {
                arrangedCoordinates.push(remainingRingCoordinates[nearestIndex]);
                remainingRingCoordinates.splice(nearestIndex, 1);
            }
        });
        // ringCoordinates.forEach((vertex) => arrangedCoordinates.unshift(vertex));
        return arrangedCoordinates;
    },

    buildOffsetPolygon(
        leadInPath: number[][] | undefined,
        leadOutPath: number[][] | undefined,
        isAntiClockwise: boolean,
        SR: SpatialReference | undefined
    ) {
        if (!leadInPath || !leadOutPath || leadInPath.length < 2 || leadOutPath.length < 2) {
            return null;
        }
        // isAntiClockwise condition is required to return the ring in clockwise direction
        // because esri always have a clockwise outer ring
        return new Polygon({
            rings: [
                isAntiClockwise
                    ? [leadInPath[0], leadOutPath[1], leadOutPath[0], leadInPath[1], leadInPath[0]]
                    : [leadInPath[0], leadInPath[1], leadOutPath[0], leadOutPath[1], leadInPath[0]]
            ],
            spatialReference: SR
        });
    },

    generateOffsetGeometry(
        geometry: Geometry,
        clickedVertices: number[][],
        isAntiClockwise: boolean,
        leadInValue: number,
        leadOutValue: number
    ) {
        const ringCoordinates = (geometry as Polygon).rings[0].slice(0, 4);  //  need only 4 coordinates and the last coordinates is ignored as it is same as the first coordinates

        // arranging the coordinates of the ring as it is clicked by the user to draw the polygon
        const arrangedCoordinates = this.arrangeRingCoordinatesWithClickedVertices(
            ringCoordinates,
            clickedVertices
        );

        // Generate offset line for lead-in edge
        const offsetLine1 = offsetOperator.execute(
            new Polyline({
                paths: [[arrangedCoordinates[0], arrangedCoordinates[1]]],
                spatialReference: geometry.spatialReference,
            }),
            leadInValue,
            { unit: 'feet' }
        ) as Polyline;

        // Generate offset line for lead-out edge
        const offsetLine2 = offsetOperator.execute(
            new Polyline({
                paths: [[arrangedCoordinates[2], arrangedCoordinates[3]]],
                spatialReference: geometry.spatialReference,
            }),
            leadOutValue,
            { unit: 'feet' }
        ) as Polyline;

        const offSetPolygon = this.buildOffsetPolygon(
            offsetLine1?.paths[0],
            offsetLine2?.paths[0],
            isAntiClockwise,
            geometry.spatialReference
        );
        return offSetPolygon;
    },

    getOffsetGeometryForGraphic(graphic: Graphic, clickedVertices: any[], leadInValue: number, leadOutValue: number, layerSpatialReference: SpatialReference) {
        if (
            graphic.attributes?.leadInValue === -1 ||
            graphic.attributes?.leadOutValue === -1 ||
            (leadInValue === 0 && leadOutValue === 0) ||
            (graphic.geometry as Polygon).rings[0].length !== 5
        )
            return null;

        const geometry = graphic.geometry as Polygon;

        const convertedSRGeometry = this.projectGeometryToLayerSpatialReference(
            geometry,
            layerSpatialReference
        );

        const convertedClickedVertices = this.updateVerticesSpatialReferenceToUTMZoneSR(
            clickedVertices,
            geometry.spatialReference,
            layerSpatialReference
        );
        const { isAntiClockwise } = graphic.attributes;

        const originalArea = geometryEngine.planarArea(convertedSRGeometry, 'square-meters');

        let offsetGeo = this.generateOffsetGeometry(
            convertedSRGeometry,
            convertedClickedVertices,
            isAntiClockwise,
            leadInValue,
            leadOutValue
        );
        if (!offsetGeo) return null;

        if (geometryEngine.planarArea(offsetGeo, 'square-meters') < originalArea) {
            offsetGeo = this.generateOffsetGeometry(
                convertedSRGeometry,
                convertedClickedVertices,
                isAntiClockwise,
                -leadInValue,
                -leadOutValue
            );
            if (!offsetGeo || geometryEngine.planarArea(offsetGeo, 'square-meters') <= originalArea) {
                return null;
            }
        }

        return offsetGeo;
    },

    projectGeometryToLayerSpatialReference(geometry: any, layerSpatialReference: SpatialReference) {
        if (!geometry) {
            return geometry;
        }
        return geometry?.spatialReference?.wkid !== layerSpatialReference.wkid
            ? projection.project(geometry, layerSpatialReference)
            : geometry;
    },

    updateVerticesSpatialReferenceToUTMZoneSR(
        vertices: number[][],
        SR: SpatialReference,
        layerSpatialReference: SpatialReference
    ): number[][] {
        return vertices.map(([x, y]) => {
            const point = new Point({
                x,
                y,
                spatialReference: SR,
            });

            const projectedPoint = this.projectGeometryToLayerSpatialReference(point, layerSpatialReference) as Point;

            return [projectedPoint.x, projectedPoint.y];
        });
    },

    isAntiClockwise(ring: any[]) {
        let sum = 0;
        const n = ring.length;
        for (let i = 0; i < n; i++) {
            const [x1, y1] = ring[i];
            const [x2, y2] = ring[(i + 1) % n];
            sum += x1 * y2 - x2 * y1;
        }
        return sum > 0;
    },

    createCustomButton(customButton: {id: string, label: string}, gridGraphicsLength: number): [HTMLDivElement, HTMLSpanElement] {
        const innerContent: Record<string, string> = {
            'Cut Graphics': '<calcite-icon icon="scissors" />',
            'Copy Graphics': '<calcite-icon icon="copy" />',
            'Save Graphics': '<calcite-icon icon="save" />',
            'Discard Changes': '<calcite-icon icon="x-circle-f" />',
            Validate: '<calcite-icon icon="check-circle" />',
            Delete: '<calcite-icon icon="trash" />',
            Exit: '<calcite-icon icon="x-circle-f" />',
            'Redraw Boundary': '<calcite-icon icon="reshape-tool" />',
        };

        const button: HTMLDivElement = document.createElement('div');
        button.id = customButton.id;
        button.className = 'custom-button';
        button.innerHTML = innerContent[customButton.label] || '';
        button.style.display =
            customButton.id !== CUSTOM_BUTTON.SAVE.id &&
                customButton.id !== CUSTOM_BUTTON.DISCARD.id &&
                gridGraphicsLength > 0
                ? 'block'
                : 'none';

        const tooltip: HTMLSpanElement = document.createElement('span');
        tooltip.textContent = customButton.label;
        tooltip.className = 'custom-tooltip';
        return [button, tooltip];
    },

    shouldEnableTooltipOptions(clickType: string) {
        return clickType === MAPOPERATION_TYPE.CREATE_PRESCRIPTION ||
            clickType === MAPOPERATION_TYPE.DRAW_BOUNDARY ||
            clickType === MAPOPERATION_TYPE.DRAW_SWATH_LINES ? true : false;
    },

    generateRxDefaultColor(index: number): [number, number, number, number] {
        // The golden angle spreads consecutive hues around the color wheel.
        const GOLDEN_ANGLE = 180 * (3 - Math.sqrt(5));
        const hue = ((index * GOLDEN_ANGLE) % 360) / 360;
        // Vary saturation and lightness to distinguish colors with similar hues.
        const saturation = 0.65 + (index % 2) * 0.15;
        const lightness = 0.45 + (Math.floor(index / 2) % 3) * 0.1;

        return this.hslToRgb(hue, saturation, lightness);
    },

    hslToRgb(h: number, s: number, l: number): [number, number, number, number] {
        let r: number, g: number, b: number;
        const hue2rgb = (p: number, q: number, t: number): number => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;

        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);

        return [
            Math.round(r * 255),
            Math.round(g * 255),
            Math.round(b * 255),
            0.5
        ];
    },

    getRxColorFromGraphic(graphicAttributes: any): [number, number, number, number] {
        return [graphicAttributes.rcolorvalue, graphicAttributes.gcolorvalue, graphicAttributes.bcolorvalue, graphicAttributes.alphavalue];
    },

    getColorFromLegendData(
        value: number,
        legendData: IHeatmapLegendData[]
    ): [number, number, number, number] {
        const match = legendData?.find((range) => {
            const from = Number(range.from);
            const to = Number(range.to);
            // Single-value range, e.g. from 1, to 1 -> matches only 1
            if (from === to) {
                return value === from;
            }
            // Normal range, e.g. from 1, to 10 -> 1 included, 10 excluded
            return value >= from && value < to;
        });
        const color = match ? helperFunctions.hexToRgba(match?.color) : [0, 0, 0, 0];
        return color as [number, number, number, number];
    },

    getHeatMapGraphic(
        graphic: Graphic,
        rawValue: string | number,
        heatmapType: string,
        selectedDataColumnName: string,
        legendData: { id: number; from: number; to: number; color: string }[]
    ): Graphic[] {
        const heatMapGraphics: Graphic[] = [];

        const geometry = graphic.geometry as Polygon;
        const centroid = geometry?.centroid ?? geometry?.extent?.center;
        // const sideA = +(
        //     this.getLineLength(
        //         (graphic.geometry as Polygon)?.rings[0][1][0],
        //         (graphic.geometry as Polygon)?.rings[0][1][1],
        //         (graphic.geometry as Polygon)?.rings[0][2][0],
        //         (graphic.geometry as Polygon)?.rings[0][2][1]
        //     ) * 3.280839895
        // ).toFixed(2);
        // const sideB = +(
        //     this.getLineLength(
        //         (graphic.geometry as Polygon)?.rings[0][0][0],
        //         (graphic.geometry as Polygon)?.rings[0][0][1],
        //         (graphic.geometry as Polygon)?.rings[0][1][0],
        //         (graphic.geometry as Polygon)?.rings[0][1][1]
        //     ) * 3.280839895
        // ).toFixed(2);
        // The smaller side is always the width, the larger side is the length
        // const width = Math.min(sideA, sideB);
        // const length = Math.max(sideA, sideB);

        if (centroid) {
            let color = [204, 165, 157, 1];
            if (heatmapType === HEATMAP_TYPE.COLOR) {
                if (rawValue.toString().trim() === '' || rawValue === null || rawValue === undefined) {
                    color = [0, 0, 0, 0];
                } else {
                    let numericValue = Number(rawValue);
                    if (Number.isNaN(numericValue)) {
                        numericValue = -1;
                    }
                    color = this.getColorFromLegendData(numericValue, legendData);
                }
            }
            const newGraphic = new Graphic({
                geometry: graphic.geometry,
                attributes: {
                    ...graphic.attributes,
                    heatmapValue: rawValue,
                    heatmapType: heatmapType,
                    selectedDataColumn: selectedDataColumnName,
                },
                symbol: {
                    type: 'simple-fill',
                    color: color,
                    outline: {
                        type: 'simple-line',
                        color: [0, 0, 0, 0.4],
                        width: 1,
                    },
                } as any,
            });

            const textGraphic = new Graphic({
                geometry: centroid,
                attributes: {
                    rangevalue: graphic.attributes.rangevalue,
                    rowvalue: graphic.attributes.rowvalue,
                    heatmapValue: rawValue,
                    selectedDataColumn: selectedDataColumnName,
                    heatmapType: heatmapType,
                    // length: length,
                    // width: width,
                },
                symbol: {
                    type: 'text',
                    color: [0, 0, 0, 1],
                    angle: 90,
                    text:
                        heatmapType === HEATMAP_TYPE.COLOR
                            ? `${graphic.attributes.rangevalue}-${graphic.attributes.rowvalue}`
                            : String(rawValue),
                    font: {
                        size: 10,
                        family: 'sans-serif',
                        weight: 'bold',
                    },
                } as any,
            });
            heatMapGraphics.push(newGraphic, textGraphic);
        }
        return heatMapGraphics;
    },

    createHeatmapLegendElement(
        legendData: { id: number; from: number; to: number; color: string }[],
        containerId: string
    ): HTMLDivElement {
        const legendContainer = document.createElement('div');
        legendContainer.id = containerId;
        Object.assign(legendContainer.style, {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '220px',
            overflowY: 'auto',
            gap: '4px',
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            fontSize: '14px',
            padding: '10px 15px 10px 10px',
        });

        legendData.forEach((item) => {
            const row = document.createElement('div');
            Object.assign(row.style, { display: 'flex', alignItems: 'center', gap: '8px' });

            const colorSpan = document.createElement('span');
            Object.assign(colorSpan.style, {
                display: 'inline-block',
                width: '16px',
                height: '12px',
                background: item.color,
                border: '1px solid rgba(0,0,0,0.4)',
            });

            const label = document.createElement('span');
            label.textContent = `${item.from ?? 0} - ${item.to ?? 0}`;

            row.appendChild(colorSpan);
            row.appendChild(label);
            legendContainer.appendChild(row);
        });

        return legendContainer;
    },

    async createAndExportExcelForTextHeatmap(
        textGraphics: Graphic[],
        locationName: string
    ) {
        const workbook = new ExcelJS.Workbook();
        // const mapSheet = workbook.addWorksheet("Field Map");
        const worksheet = workbook.addWorksheet(locationName);
        const ranges = Array.from(new Set(textGraphics.map(g => g.attributes.rangevalue)))
            .sort((a, b) => a - b);

        const rows = Array.from(new Set(textGraphics.map(g => g.attributes.rowvalue)))
            .sort((a, b) => a - b);

        // Header row
        worksheet.getCell(1, 1).value = "Column";
        worksheet.getCell(1, 2).value = "Range/Row";

        rows.forEach((rowValue, index) => {
            worksheet.getCell(1, index + 3).value = rowValue;
        });

        // Build lookup map
        const lookup = new Map<string, string | number>();
        textGraphics.forEach(g => {
            const key = `${g.attributes.rangevalue}-${g.attributes.rowvalue}`;
            lookup.set(key, isNaN(Number(g.attributes.heatmapValue)) ? String(g.attributes.heatmapValue) : Number(g.attributes.heatmapValue));
        });

        // Fill table
        ranges.forEach((rangeValue, rIndex) => {
            const excelRow = rIndex + 2;

            worksheet.getCell(excelRow, 1).value = textGraphics[0].attributes.selectedDataColumn;
            worksheet.getCell(excelRow, 2).value = rangeValue;

            rows.forEach((rowValue, cIndex) => {
                const value = lookup.get(`${rangeValue}-${rowValue}`);
                worksheet.getCell(excelRow, cIndex + 3).value = value ?? "";
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();

        saveAs(
            new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            `${locationName} Map.xlsx`
        );
    },

    createUTMZoneElement(
        utmZone: string,
        containerId: string
    ): HTMLDivElement {
        const utmZoneContainer = document.createElement('div');
        utmZoneContainer.id = containerId;
        Object.assign(utmZoneContainer.style, {
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
            fontSize: '16px',
            padding: '10px',
        });

        utmZoneContainer.textContent = utmZone;

        return utmZoneContainer;
    },



    // Commented code for color heatmap export

    // async createAndExportExcelForColorHeatmap(graphics: Graphic[]) {
    //     // Get the extreme min and max of the entire graphics
    //     const bounds = this.getCombinedBounds(graphics);
    //     if (!bounds) {
    //         throw new Error("Unable to determine graphic extent.");
    //     }

    //     const padding = 40;
    //     const MAX_DIMENSION = 5000; // px, safety cap
    //     const MIN_DIMENSION = 2000;  // px, so tiny fields still look decent
    //     let scale = 40;

    //     const geoWidth = Math.max(bounds.xmax - bounds.xmin, 1e-6);
    //     const geoHeight = Math.max(bounds.ymax - bounds.ymin, 1e-6);
    //     const aspect = geoWidth / geoHeight;

    //     let canvasWidth = geoWidth * scale + padding * 2;
    //     let canvasHeight = geoHeight * scale + padding * 2;

    //     // // Clamp to MAX_DIMENSION while preserving aspect ratio.
    //     // if (canvasWidth > MAX_DIMENSION || canvasHeight > MAX_DIMENSION) {
    //     //     if (aspect >= 1) {
    //     //         canvasWidth = MAX_DIMENSION;
    //     //         scale = (MAX_DIMENSION - (2 * padding)) / geoWidth;
    //     //         canvasHeight = geoHeight * scale + (2 * padding);
    //     //     } else {
    //     //         canvasHeight = MAX_DIMENSION;
    //     //         scale = (MAX_DIMENSION - (2 * padding)) / geoHeight;
    //     //         canvasWidth = geoWidth * scale + (2 * padding);
    //     //     }
    //     // }

    //     // // Ensure minimum readable size.

    //     // // need to check with && beacuse if width/height is too large, 
    //     // // then even if we fix width to max the height might be less then min
    //     // if (canvasWidth < MIN_DIMENSION && canvasHeight < MIN_DIMENSION) {
    //     //     if (aspect >= 1) {
    //     //         canvasWidth = MIN_DIMENSION;
    //     //         scale = (MIN_DIMENSION - (2 * padding)) / geoWidth;
    //     //         canvasHeight = geoHeight * scale + (2 * padding);
    //     //     } else {
    //     //         canvasHeight = MIN_DIMENSION;
    //     //         scale = (MIN_DIMENSION - (2 * padding)) / geoHeight;
    //     //         canvasWidth = geoWidth * scale + (2 * padding);
    //     //     }
    //     // }

    //     canvasWidth = Math.round(canvasWidth);
    //     canvasHeight = Math.round(canvasHeight);

    //     const canvas = document.createElement("canvas");
    //     canvas.width = canvasWidth;
    //     canvas.height = canvasHeight;

    //     const ctx = canvas.getContext("2d");

    //     if (!ctx) {
    //         throw new Error("Unable to create canvas context.");
    //     }

    //     // Optional white background so the exported image isn't transparent.
    //     ctx.fillStyle = "#ffffff";
    //     ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    //     // const transform = this.createTransform(
    //     //     bounds,
    //     //     canvasWidth,
    //     //     canvasHeight,
    //     //     padding
    //     // );

    //     const polygons = graphics.filter(
    //         (graphic) => graphic.geometry?.type === "polygon"
    //     );

    //     const points = graphics.filter(
    //         (graphic) => graphic.geometry?.type === "point"
    //     );

    //     for (const graphic of polygons) {
    //         this.drawPolygon(
    //             ctx,
    //             graphic,
    //             scale,
    //             padding,
    //             bounds
    //         );
    //     }

    //     for (const graphic of points) {
    //         this.drawTextPoint(
    //             ctx,
    //             graphic,
    //             scale,
    //             padding,
    //             bounds,
    //         );
    //     }

    //     const workbook = new ExcelJS.Workbook();
    //     const sheet = workbook.addWorksheet(
    //         'Color Heatmap'
    //     );

    //     const imageId = workbook.addImage({ base64: canvas.toDataURL("image/png"), extension: 'png' });
    //     sheet.addImage(imageId, {
    //         tl: { col: 0, row: 0 } as any,
    //         ext: { width: canvas.width, height: canvas.height },
    //         editAs: 'absolute',
    //     });

    //     const buffer = await workbook.xlsx.writeBuffer();
    //     saveAs(
    //         new Blob([buffer]),
    //         'color_Heatmap.xlsx'
    //     );
    // },



    // getCombinedBounds(
    //     graphics: Graphic[]
    // ): Bounds | null {
    //     let bounds: Bounds | null = null;

    //     for (const graphic of graphics) {
    //         const geometry = graphic.geometry;

    //         if (!geometry) {
    //             continue;
    //         }

    //         const extent = geometry.extent;

    //         if (!extent) {
    //             continue;
    //         }

    //         if (!bounds) {
    //             bounds = {
    //                 xmin: extent.xmin,
    //                 ymin: extent.ymin,
    //                 xmax: extent.xmax,
    //                 ymax: extent.ymax
    //             };

    //             continue;
    //         }

    //         bounds.xmin = Math.min(
    //             bounds.xmin,
    //             extent.xmin
    //         );

    //         bounds.ymin = Math.min(
    //             bounds.ymin,
    //             extent.ymin
    //         );

    //         bounds.xmax = Math.max(
    //             bounds.xmax,
    //             extent.xmax
    //         );

    //         bounds.ymax = Math.max(
    //             bounds.ymax,
    //             extent.ymax
    //         );
    //     }

    //     return bounds;
    // },

    // // createTransform(
    // //     bounds: Bounds,
    // //     width: number,
    // //     height: number,
    // //     padding: number
    // // ): Transform {
    // //     const geographicWidth =
    // //         bounds.xmax - bounds.xmin;

    // //     const geographicHeight =
    // //         bounds.ymax - bounds.ymin;

    // //     /*
    // //      * Avoid division by zero if all graphics have
    // //      * exactly the same x or y coordinate.
    // //      */
    // //     const safeWidth =
    // //         geographicWidth === 0
    // //             ? 1
    // //             : geographicWidth;

    // //     const safeHeight =
    // //         geographicHeight === 0
    // //             ? 1
    // //             : geographicHeight;

    // //     const availableWidth =
    // //         width - padding * 2;

    // //     const availableHeight =
    // //         height - padding * 2;

    // //     /*
    // //      * Use the same scale for X and Y.
    // //      *
    // //      * This is important because otherwise the geometry
    // //      * could become stretched.
    // //      */
    // //     const scale = Math.min(
    // //         availableWidth / safeWidth,
    // //         availableHeight / safeHeight
    // //     );

    // //     /*
    // //      * Calculate the actual rendered dimensions.
    // //      */
    // //     const renderedWidth =
    // //         safeWidth * scale;

    // //     const renderedHeight =
    // //         safeHeight * scale;

    // //     /*
    // //      * Center the graphics inside the canvas.
    // //      */
    // //     const offsetX =
    // //         padding +
    // //         (availableWidth - renderedWidth) / 2;

    // //     const offsetY =
    // //         padding +
    // //         (availableHeight - renderedHeight) / 2;

    // //     return {
    // //         scale,
    // //         offsetX,
    // //         offsetY,
    // //         bounds
    // //     };
    // // },

    // drawPolygon(
    //     ctx: CanvasRenderingContext2D,
    //     graphic: Graphic,
    //     scale: number,
    //     padding: number,
    //     bounds: Bounds,
    // ) {
    //     const polygon =
    //         graphic.geometry as Polygon;

    //     const symbol =
    //         graphic.symbol as SimpleFillSymbol;

    //     if (!polygon?.rings?.length) {
    //         return;
    //     }
    //     ctx.save();
    //     ctx.beginPath();
    //     for (const ring of polygon.rings) {
    //         if (!ring || ring.length === 0) {
    //             continue;
    //         }

    //         const firstPoint =
    //             this.projectToCanvas(
    //                 ring[0][0],
    //                 ring[0][1],
    //                 scale,
    //                 padding,
    //                 bounds
    //             );

    //         ctx.moveTo(
    //             firstPoint.x,
    //             firstPoint.y
    //         );

    //         for (let i = 1; i < ring.length; i++) {
    //             const point =
    //                 this.projectToCanvas(
    //                     ring[i][0],
    //                     ring[i][1],
    //                     scale,
    //                     padding,
    //                     bounds
    //                 );

    //             ctx.lineTo(
    //                 point.x,
    //                 point.y
    //             );
    //         }

    //         ctx.closePath();
    //     }
    //     const fillColor =
    //         this.colorToRgba(symbol?.color);

    //     if (fillColor) {
    //         ctx.fillStyle = fillColor;
    //         ctx.fill("evenodd");
    //     }
    //     const outline =
    //         symbol?.outline;

    //     if (outline) {
    //         const outlineColor =
    //             this.colorToRgba(outline.color);
    //         if (outlineColor) {
    //             ctx.strokeStyle =
    //                 outlineColor;
    //         }
    //         ctx.lineWidth =
    //             outline.width ?? 1;
    //         ctx.stroke();
    //     }
    //     ctx.restore();
    // },

    // drawTextPoint(
    //     ctx: CanvasRenderingContext2D,
    //     graphic: Graphic,
    //     scale: number,
    //     padding: number,
    //     bounds: Bounds
    // ) {
    //     const point =
    //         graphic.geometry as Point;

    //     const symbol =
    //         graphic.symbol as TextSymbol;

    //     if (!point || !symbol) {
    //         return;
    //     }

    //     const position =
    //         this.projectToCanvas(
    //             point.x,
    //             point.y,
    //             scale,
    //             padding,
    //             bounds
    //         );

    //     const text =
    //         symbol.text ?? "";

    //     if (!text) {
    //         return;
    //     }

    //     const widthPx = graphic.attributes.width * FEET_METER_CONSTANT * scale;
    //     // *0.2 So that if widthPx is 60 font px is 12, 90 :- 18,  120 :- 24 etc.
    //     const fontSizePx = Math.round(
    //         // Math.min(
    //         //     Math.max(widthPx * 0.2, 10),
    //         //     24
    //         // )
    //         Math.max(widthPx * 0.2, 10),
    //     );

    //     const fontSize = fontSizePx;
    //     // const fontSize = 12;
    //     const fontFamily = "Arial";
    //     const fontWeight = "normal";
    //     const fontStyle = "normal";

    //     ctx.save();
    //     ctx.font = [
    //         fontStyle,
    //         fontWeight,
    //         `${fontSize}px`,
    //         fontFamily
    //     ].join(" ");

    //     switch (
    //     symbol.horizontalAlignment
    //     ) {
    //         case "left":
    //             ctx.textAlign = "left";
    //             break;

    //         case "right":
    //             ctx.textAlign = "right";
    //             break;

    //         case "center":
    //         default:
    //             ctx.textAlign = "center";
    //             break;
    //     }

    //     switch (
    //     symbol.verticalAlignment
    //     ) {
    //         case "top":
    //             ctx.textBaseline = "top";
    //             break;

    //         case "bottom":
    //             ctx.textBaseline = "bottom";
    //             break;

    //         case "middle":
    //         default:
    //             ctx.textBaseline = "middle";
    //             break;
    //     }

    //     ctx.fillStyle = "#000000";
    //     ctx.fillText(
    //         text,
    //         position.x,
    //         position.y
    //     );

    //     ctx.restore();
    // },

    // projectToCanvas(
    //     x: number,
    //     y: number,
    //     scale: number,
    //     padding: number,
    //     bounds: Bounds
    // ) {

    //     const canvasX =
    //         padding +
    //         (x - bounds.xmin) * scale;
    //     // because the canvas Y axis is inverted, we need to flip the Y coordinate. 
    //     // means calculate the distance of Y' from the bottom of the canvas
    //     // and bottom of canvas is at ymax of geo plane.
    //     const canvasY =
    //         padding +
    //         (bounds.ymax - y) * scale;

    //     return {
    //         x: canvasX,
    //         y: canvasY
    //     };
    // },

    // colorToRgba(
    //     color: any,
    // ): string | null {
    //     if (!color) {
    //         return null;
    //     }
    //     if (Array.isArray(color)) {
    //         const r = color[0] ?? 0;
    //         const g = color[1] ?? 0;
    //         const b = color[2] ?? 0;
    //         const alpha =
    //             color.length > 3
    //                 ? color[3]
    //                 : 1;

    //         return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    //     }
    //     if (typeof color === "string") {
    //         return color;
    //     }
    //     if (
    //         typeof color.toRgba === "function"
    //     ) {
    //         const rgba =
    //             color.toRgba();

    //         return `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]
    //             }, ${rgba[3]})`;
    //     }

    //     return null;
    // },

    // getFullGeometryExtent(graphics: Graphic[]): Extent {
    //     const geometries = graphics
    //         .map((g) => g.geometry)
    //         .filter((geom): geom is Geometry => !!geom);
    //     let fullExtent = geometries[0]?.extent?.clone();
    //     geometries.forEach((geom) => {
    //         if (geom.extent) {
    //             fullExtent = fullExtent ? fullExtent.union(geom.extent) : geom.extent.clone();
    //         }
    //     });
    //     return fullExtent;
    // },

    // getScreenAreaForScreenshot(topLeft: IScreenPoint, bottomRight: IScreenPoint): IScreenArea | null {
    //     const x = Math.max(0, Math.min(topLeft.x, bottomRight.x) - SCREEN_PADDING_PX);
    //     const y = Math.max(0, Math.min(topLeft.y, bottomRight.y) - SCREEN_PADDING_PX);
    //     const width = Math.abs(bottomRight.x - topLeft.x) + SCREEN_PADDING_PX * 2;
    //     const height = Math.abs(bottomRight.y - topLeft.y) + SCREEN_PADDING_PX * 2;
    //     if (width > 0 && height > 0) {
    //         return { x, y, width, height };
    //     }
    //     return null;
    // }
};
